/**
 * Iter 231.6 — Member admin panel
 *
 * Mounted inside `UsersRolesManager.jsx` (`/tools/users-roles → Users` tab).
 * Replaces the legacy "permissions overrides only" expansion with full
 * owner/admin/manager surface for managing every staff member:
 *
 *   • Profile: name, phone, avatar (upload / clear)
 *   • Login email (primary) — calls POST /api/team/persons/{pid}/primary-email
 *     so the user record's `email` is updated AND old email is demoted to
 *     personal_emails for audit (HR-grade swap, not a silent overwrite).
 *   • Personal / HR emails — add / promote / remove via the `team/persons`
 *     endpoints (these are the HR-side records that survive email recycling).
 *   • Role — dropdown, blocked transitions enforced server-side.
 *   • Password reset — set-password inline (4+ chars).
 *   • Account status — activate / deactivate.
 *
 * Trust Contract: no popup overlays, no native browser confirmations.
 * Two-tap inline confirms for destructive paths.
 */
import { useEffect, useRef, useState } from "react";
import {
  User, Mail, Phone, KeyRound, Shield, ImageIcon, Pencil, Check, X,
  Loader2, AlertTriangle, Trash2, UserMinus, UserCheck, ArrowUpFromLine,
  Plus,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "../ui/sonner";

const ROLE_OPTIONS = [
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
  { value: "technician", label: "Technician" },
  { value: "rep", label: "Rep" },
];

export default function MemberAdminPanel({
  user,           // the row's user doc from /api/users
  meRole,         // "owner" | "admin" | "manager"
  meId,           // current viewer's id
  onChanged,      // () => void — refetch parent list
  permissionsSlot, // optional JSX — rendered right after Role section (owner-only)
}) {
  const isOwnerRow = user.role === "owner";
  const isSelf = user.id === meId;
  // Owner row is read-only for everyone except themselves (already gated
  // server-side, but we hide the panel to avoid confusion).
  if (isOwnerRow && !isSelf) {
    return (
      <div className="text-xs text-amber-300 inline-flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        Owner accounts can only be edited by the Owner themselves.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`member-admin-${user.id}`}>
      <ProfileSection user={user} onChanged={onChanged} />
      <LoginEmailSection user={user} onChanged={onChanged} />
      <PersonalEmailsSection user={user} onChanged={onChanged} />
      <RoleSection user={user} meRole={meRole} isSelf={isSelf} onChanged={onChanged} />
      {permissionsSlot}
      <AttendanceConfigSection user={user} onChanged={onChanged} />
      <PasswordSection user={user} onChanged={onChanged} />
      <ActiveStatusSection user={user} isSelf={isSelf} onChanged={onChanged} />
    </div>
  );
}

/* ─── [Iter 355 — 2026-02-26] Attendance / overtime config ─── */
/**
 * Compact editor for a member's working-hours + overtime multiplier
 * configuration. Dirk: "In team member setup we must be able to
 * configure working hours and overtime tems such as 1.5x on Saturday
 * and 2x on Sunday and public holidays". Every field is optional —
 * blank means "use the tenant BCEA default" (45h/w, 1.5× weekday OT,
 * 1.5× Sat, 2× Sun, 2× public holiday, 8h/day).
 *
 * The panel stays collapsed by default so it doesn't clutter the
 * primary profile view; click "Overtime setup" to expand.
 */
function AttendanceConfigSection({ user, onChanged }) {
  const cfg = user.attendance_config || {};
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dailyHours, setDailyHours] = useState(cfg.daily_hours ?? 8);
  const [weeklyHours, setWeeklyHours] = useState(cfg.weekly_ordinary_hours ?? 45);
  const [weekdayMul, setWeekdayMul] = useState(cfg.weekday_ot_mul ?? 1.5);
  const [satMul, setSatMul] = useState(cfg.saturday_mul ?? 1.5);
  const [sunMul, setSunMul] = useState(cfg.sunday_mul ?? 2);
  const [holMul, setHolMul] = useState(cfg.holiday_mul ?? 2);
  const [workDays, setWorkDays] = useState(
    cfg.working_days || [true, true, true, true, true, false, false],
  );
  const toggleDay = (i) => setWorkDays((d) => d.map((v, k) => (k === i ? !v : v)));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${user.id}`, {
        attendance_config: {
          daily_hours: Number(dailyHours),
          weekly_ordinary_hours: Number(weeklyHours),
          weekday_ot_mul: Number(weekdayMul),
          saturday_mul: Number(satMul),
          sunday_mul: Number(sunMul),
          holiday_mul: Number(holMul),
          working_days: workDays,
        },
      });
      toast.success("Attendance & overtime settings saved.");
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e) || "Save failed.");
    } finally {
      setBusy(false);
    }
  };
  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <section className="rounded border border-white/5 p-3" data-testid={`att-cfg-${user.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`att-cfg-toggle-${user.id}`}
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-white">
          <Shield className="w-4 h-4 text-[#1FB6C1]" /> Overtime setup
        </span>
        <span className="text-[10px] text-[#647087]">
          {open ? "hide" : "edit hours, Sat/Sun/holiday multipliers"}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-3 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Working days</div>
            <div className="flex gap-1 flex-wrap">
              {DAY_LABELS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={`px-2 py-1 rounded border text-[10px] ${
                    workDays[i]
                      ? "border-[#1FB6C1]/40 bg-[#1FB6C1]/15 text-[#a0e6ea]"
                      : "border-white/10 text-[#647087] hover:text-white"
                  }`}
                  data-testid={`att-cfg-day-${i}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#647087]">Daily hours</span>
              <input type="number" step="0.5" min="0" max="24" value={dailyHours}
                onChange={(e) => setDailyHours(e.target.value)}
                className="hv-input text-xs" data-testid={`att-cfg-daily-${user.id}`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#647087]">Weekly threshold (hours before OT)</span>
              <input type="number" step="0.5" min="0" max="168" value={weeklyHours}
                onChange={(e) => setWeeklyHours(e.target.value)}
                className="hv-input text-xs" data-testid={`att-cfg-weekly-${user.id}`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#647087]">Weekday OT ×</span>
              <input type="number" step="0.05" min="0.5" max="5" value={weekdayMul}
                onChange={(e) => setWeekdayMul(e.target.value)}
                className="hv-input text-xs" data-testid={`att-cfg-weekday-mul-${user.id}`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#647087]">Saturday ×</span>
              <input type="number" step="0.05" min="0.5" max="5" value={satMul}
                onChange={(e) => setSatMul(e.target.value)}
                className="hv-input text-xs" data-testid={`att-cfg-sat-mul-${user.id}`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#647087]">Sunday ×</span>
              <input type="number" step="0.05" min="0.5" max="5" value={sunMul}
                onChange={(e) => setSunMul(e.target.value)}
                className="hv-input text-xs" data-testid={`att-cfg-sun-mul-${user.id}`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#647087]">Public holiday ×</span>
              <input type="number" step="0.05" min="0.5" max="5" value={holMul}
                onChange={(e) => setHolMul(e.target.value)}
                className="hv-input text-xs" data-testid={`att-cfg-hol-mul-${user.id}`} />
            </label>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid={`att-cfg-save-${user.id}`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Save overtime setup
            </button>
          </div>
          <p className="text-[10px] text-[#647087] leading-relaxed">
            These settings drive the &ldquo;Hours worked&rdquo; split on the
            attendance register. Blank / defaults use SA BCEA norms
            (45 h weekly, 1.5× Sat, 2× Sun &amp; holidays).
          </p>
        </div>
      )}
    </section>
  );
}

/* ─── Profile (name / phone / avatar) ─── */
function ProfileSection({ user, onChanged }) {
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const fileRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${user.id}`, {
        name: name.trim() || null,
        phone: phone.trim() || null,
      });
      toast.success("Profile saved.");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not save profile"));
    } finally { setBusy(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Avatar must be ≤ 10 MB.");
      return;
    }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      await api.post(`/users/${user.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar updated.");
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not upload avatar"));
    } finally { setAvatarBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete(`/users/${user.id}/avatar`);
      toast.success("Avatar removed.");
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not remove avatar"));
    } finally { setAvatarBusy(false); }
  };

  return (
    <SectionCard
      icon={User}
      title="Profile"
      testId={`profile-${user.id}`}
      action={!editing && (
        <button
          onClick={() => setEditing(true)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`profile-edit-${user.id}`}
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
      )}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="relative">
          <div className="w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] flex items-center justify-center text-xl font-bold uppercase text-[#0a0b0e]">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              (user.name || user.email || "?").slice(0, 1)
            )}
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#1FB6C1] text-[#0a0b0e] flex items-center justify-center hover:scale-110 transition"
            disabled={avatarBusy}
            title="Upload avatar"
            data-testid={`profile-avatar-upload-${user.id}`}
          >
            {avatarBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadAvatar(e.target.files?.[0])}
            data-testid={`profile-avatar-input-${user.id}`}
          />
        </div>
        <div className="flex-1 min-w-[200px] space-y-2">
          {editing ? (
            <>
              <Field label="Display name">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="hv-input text-sm"
                  data-testid={`profile-name-input-${user.id}`}
                />
              </Field>
              <Field label="Phone">
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="hv-input text-sm"
                  placeholder="+27 …"
                  data-testid={`profile-phone-input-${user.id}`}
                />
              </Field>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => { setEditing(false); setName(user.name || ""); setPhone(user.phone || ""); }}
                  className="hv-btn hv-btn-ghost text-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={busy}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid={`profile-save-${user.id}`}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                </button>
              </div>
            </>
          ) : (
            <>
              <ReadRow label="Name" value={user.name || "—"} />
              <ReadRow label="Phone" value={user.phone || "—"} />
              {user.avatar_url && (
                <button
                  onClick={deleteAvatar}
                  className="text-[10px] uppercase tracking-wider text-rose-300 hover:text-rose-200"
                  data-testid={`profile-avatar-delete-${user.id}`}
                >
                  <Trash2 className="w-2.5 h-2.5 inline mr-0.5" /> Remove avatar
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

/* ─── Login email (primary) ─── */
function LoginEmailSection({ user, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [demote, setDemote] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const pid = user.person_id;
  const hasPid = Boolean(pid);

  const open = (prefill = "") => {
    setDraft(prefill);
    setDemote(true);
    setConfirm(false);
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft(""); setConfirm(false); };

  const save = async () => {
    const em = (draft || "").trim().toLowerCase();
    if (!em || !em.includes("@")) return toast.error("Enter a valid email.");
    if (em === (user.email || "").toLowerCase()) return toast.error("That's already the login email.");
    if (!confirm) { setConfirm(true); return; }
    setBusy(true);
    try {
      if (hasPid) {
        // HR-grade swap — also demotes old to personal_emails.
        await api.post(`/team/persons/${pid}/primary-email`, {
          email: em,
          demote_old_to_personal: demote,
        });
      } else {
        // Legacy user without a person record — fall back to a plain
        // PUT /users/{uid}. We log an audit note in the BE via the
        // existing update_user endpoint.
        await api.put(`/users/${user.id}`, { email: em });
      }
      toast.success(`Login email changed to ${em}.`);
      cancel();
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not change login email"));
    } finally { setBusy(false); }
  };

  return (
    <SectionCard
      icon={Mail}
      title="Login email"
      subtitle="The email the member uses to sign in. Changing this here changes their login."
      testId={`login-email-${user.id}`}
      action={!editing && (
        <button
          onClick={() => open()}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`login-email-change-btn-${user.id}`}
        >
          <Pencil className="w-3 h-3" /> Change
        </button>
      )}
    >
      <div className="flex items-center gap-2 text-sm flex-wrap">
        <Mail className="w-3.5 h-3.5 text-[#647087]" />
        <span data-testid={`login-email-value-${user.id}`}>{user.email}</span>
        <span className="text-[10px] uppercase tracking-wider text-[#647087]">· primary</span>
      </div>
      {editing && (
        <div
          className="mt-3 border border-[#1FB6C1]/30 bg-[#1FB6C1]/[0.04] rounded p-3 space-y-2"
          data-testid={`login-email-panel-${user.id}`}
        >
          <Field label="New login email *">
            <input
              type="email"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setConfirm(false); }}
              placeholder="new-login@hidroverse.co.za"
              className="hv-input text-sm"
              autoFocus
              data-testid={`login-email-input-${user.id}`}
            />
          </Field>
          {hasPid && (
            <label className="flex items-center gap-1.5 text-[11px] text-[#cbd2df] cursor-pointer">
              <input
                type="checkbox"
                checked={demote}
                onChange={(e) => setDemote(e.target.checked)}
                className="h-3 w-3 accent-[#1FB6C1]"
                data-testid={`login-email-demote-${user.id}`}
              />
              Keep "{user.email}" as a personal email
            </label>
          )}
          {confirm && (
            <div className="text-[11px] text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-1.5 flex items-start gap-1.5">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                Confirm — <strong>{draft.trim().toLowerCase()}</strong> will become the login email.
                Existing sessions stay alive; the next sign-in MUST use the new email.
              </span>
            </div>
          )}
          <div className="flex gap-1 justify-end">
            <button
              onClick={cancel}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`login-email-cancel-${user.id}`}
            >Cancel</button>
            <button
              onClick={save}
              disabled={busy || !draft.trim()}
              className={`hv-btn text-xs ${confirm ? "bg-amber-500/80 hover:bg-amber-500 text-white border border-amber-500/60" : "hv-btn-primary"}`}
              data-testid={`login-email-save-${user.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {confirm ? "Yes — change login" : "Save"}
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

/* ─── Personal / HR emails ─── */
function PersonalEmailsSection({ user, onChanged }) {
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const pid = user.person_id;
  const hasPid = Boolean(pid);

  const load = async () => {
    if (!hasPid) return;
    setLoading(true);
    try {
      // Iter 231.6 — `/team/persons/by-user/{uid}` returns
      // `{ person_id, person }` so the existing IdentityBlock pattern
      // works. We grab the person doc for personal_emails.
      const { data } = await api.get(`/team/persons/by-user/${user.id}`);
      setIdentity({ person: data.person });
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user.id]);

  const add = async () => {
    const em = (newEmail || "").trim().toLowerCase();
    if (!em || !em.includes("@")) return toast.error("Enter a valid email.");
    setBusy(true);
    try {
      await api.post(`/team/persons/${pid}/personal-emails`, { email: em });
      toast.success("Personal email added.");
      setNewEmail("");
      setAdding(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not add email"));
    } finally { setBusy(false); }
  };
  const remove = async (em) => {
    setBusy(true);
    try {
      await api.delete(`/team/persons/${pid}/personal-emails/${encodeURIComponent(em)}`);
      toast.success("Personal email removed.");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not remove email"));
    } finally { setBusy(false); }
  };
  const promote = async (em) => {
    setBusy(true);
    try {
      await api.post(`/team/persons/${pid}/primary-email`, {
        email: em, demote_old_to_personal: true,
      });
      toast.success(`${em} is now the login email.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not promote email"));
    } finally { setBusy(false); }
  };

  if (!hasPid) {
    return (
      <SectionCard
        icon={Mail}
        title="Personal / HR emails"
        testId={`personal-emails-${user.id}`}
      >
        <p className="text-xs text-[#647087] italic">
          Personal HR emails attach to a "person" record (so they survive
          email recycling). This member doesn't have one yet — opening
          their profile from the team page will create it automatically.
        </p>
      </SectionCard>
    );
  }

  const personal = (identity?.person?.personal_emails || []);

  return (
    <SectionCard
      icon={Mail}
      title="Personal / HR emails"
      subtitle="Private addresses linked to this member's HR record. Don't surface to customers."
      testId={`personal-emails-${user.id}`}
      action={!adding && (
        <button
          onClick={() => setAdding(true)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`personal-emails-add-btn-${user.id}`}
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      )}
    >
      {loading ? (
        <div className="text-xs text-[#647087]">Loading…</div>
      ) : (
        <>
          {personal.length === 0 ? (
            <div className="text-xs text-[#647087] italic">None yet.</div>
          ) : (
            <ul className="space-y-1">
              {personal.map((em) => (
                <li
                  key={em}
                  className="flex items-center gap-2 text-sm border border-white/5 rounded px-2 py-1.5 flex-wrap"
                  data-testid={`personal-email-row-${user.id}-${em}`}
                >
                  <Mail className="w-3 h-3 text-[#647087]" />
                  <span className="font-mono text-xs flex-1 min-w-0 truncate">{em}</span>
                  <button
                    onClick={() => promote(em)}
                    disabled={busy}
                    className="hv-btn hv-btn-ghost text-[10px] py-0.5"
                    title="Promote to login email"
                    data-testid={`personal-email-promote-${user.id}-${em}`}
                  >
                    <ArrowUpFromLine className="w-2.5 h-2.5" /> Promote
                  </button>
                  <button
                    onClick={() => remove(em)}
                    disabled={busy}
                    className="hv-btn hv-btn-ghost text-[10px] py-0.5 text-rose-300 hover:!text-rose-200"
                    data-testid={`personal-email-remove-${user.id}-${em}`}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {adding && (
            <div className="mt-2 flex items-center gap-1 flex-wrap">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="personal@gmail.com"
                className="hv-input text-sm flex-1 min-w-[160px]"
                autoFocus
                data-testid={`personal-emails-new-input-${user.id}`}
              />
              <button
                onClick={add}
                disabled={busy || !newEmail.trim()}
                className="hv-btn hv-btn-primary text-xs"
                data-testid={`personal-emails-add-submit-${user.id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </button>
              <button
                onClick={() => { setAdding(false); setNewEmail(""); }}
                className="hv-btn hv-btn-ghost text-xs"
              >Cancel</button>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

/* ─── Role ─── */
function RoleSection({ user, meRole, isSelf, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.role);
  const [busy, setBusy] = useState(false);

  const canEdit = !isSelf && meRole !== "manager" /* manager can't promote to admin */
    ? true
    : !isSelf && meRole === "manager" && !["admin", "owner"].includes(user.role);

  if (isSelf) {
    return (
      <SectionCard icon={Shield} title="Role" testId={`role-${user.id}`}>
        <div className="text-xs text-[#a0aabf] inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/5">
            {user.role}
          </span>
          <span className="italic">You can't change your own role.</span>
        </div>
      </SectionCard>
    );
  }

  const save = async () => {
    if (draft === user.role) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.put(`/users/${user.id}`, { role: draft });
      toast.success(`Role changed to ${draft}.`);
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not change role"));
    } finally { setBusy(false); }
  };

  return (
    <SectionCard
      icon={Shield}
      title="Role"
      testId={`role-${user.id}`}
      action={!editing && canEdit && (
        <button
          onClick={() => setEditing(true)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`role-edit-btn-${user.id}`}
        >
          <Pencil className="w-3 h-3" /> Change
        </button>
      )}
    >
      {editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="hv-input text-sm"
            data-testid={`role-select-${user.id}`}
          >
            {ROLE_OPTIONS.filter((o) => {
              if (meRole === "manager" && o.value === "admin") return false;
              return true;
            }).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={save}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs"
            data-testid={`role-save-${user.id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
          </button>
          <button
            onClick={() => { setEditing(false); setDraft(user.role); }}
            className="hv-btn hv-btn-ghost text-xs"
          >Cancel</button>
        </div>
      ) : (
        <div className="text-xs">
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/5">
            {user.role}
          </span>
        </div>
      )}
    </SectionCard>
  );
}

/* ─── Password reset ─── */
function PasswordSection({ user, onChanged }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pwd.length < 4) return toast.error("Password must be ≥ 4 characters.");
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/set-password`, { new_password: pwd });
      toast.success(`Password reset. Share securely with ${user.name || user.email}.`);
      setPwd("");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not reset password"));
    } finally { setBusy(false); }
  };
  return (
    <SectionCard
      icon={KeyRound}
      title="Password"
      testId={`password-${user.id}`}
      action={!open && (
        <button
          onClick={() => setOpen(true)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`password-reset-btn-${user.id}`}
        >
          <KeyRound className="w-3 h-3" /> Reset password
        </button>
      )}
    >
      {open ? (
        <div className="flex items-center gap-1 flex-wrap">
          <input
            type="text"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="New password"
            className="hv-input text-sm font-mono flex-1 min-w-[160px]"
            autoFocus
            data-testid={`password-input-${user.id}`}
          />
          <button
            onClick={save}
            disabled={busy || pwd.length < 4}
            className="hv-btn hv-btn-primary text-xs"
            data-testid={`password-save-${user.id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
          <button
            onClick={() => { setOpen(false); setPwd(""); }}
            className="hv-btn hv-btn-ghost text-xs"
          >Cancel</button>
        </div>
      ) : (
        <p className="text-xs text-[#647087] italic">
          Resets the member's login password. Share the new one securely.
        </p>
      )}
    </SectionCard>
  );
}

/* ─── Active / inactive ─── */
function ActiveStatusSection({ user, isSelf, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const active = user.is_active !== false;
  const flip = async () => {
    if (!confirm) { setConfirm(true); return; }
    setBusy(true);
    try {
      await api.put(`/users/${user.id}`, { is_active: !active });
      toast.success(active ? "Member deactivated." : "Member re-activated.");
      setConfirm(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not change status"));
    } finally { setBusy(false); }
  };
  if (isSelf) return null;
  return (
    <SectionCard
      icon={active ? UserCheck : UserMinus}
      title="Account status"
      testId={`status-${user.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${active ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" : "border-rose-500/40 text-rose-300 bg-rose-500/5"}`}>
          {active ? "Active" : "Deactivated"}
        </span>
        {confirm && (
          <span className="text-[11px] text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-0.5">
            Confirm — {active ? "block this member's login" : "re-enable login"}?
          </span>
        )}
        <button
          onClick={flip}
          disabled={busy}
          className={`hv-btn text-xs ml-auto ${confirm ? "bg-amber-500/80 hover:bg-amber-500 text-white border border-amber-500/60" : "hv-btn-ghost"}`}
          data-testid={`status-toggle-${user.id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {confirm
            ? (active ? "Yes — deactivate" : "Yes — re-activate")
            : (active ? "Deactivate" : "Re-activate")}
        </button>
        {confirm && (
          <button
            onClick={() => setConfirm(false)}
            className="hv-btn hv-btn-ghost text-xs"
          >Cancel</button>
        )}
      </div>
    </SectionCard>
  );
}

/* ─── Helpers ─── */
function SectionCard({ icon: Icon, title, subtitle, children, action, testId }) {
  return (
    <div className="hv-card p-3 space-y-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider text-[#cbd2df] inline-flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5 text-[#1FB6C1]" /> {title}
          </div>
          {subtitle && (
            <div className="text-[10px] text-[#647087] mt-0.5">{subtitle}</div>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#a0aabf] block mb-1">{label}</label>
      {children}
    </div>
  );
}

function ReadRow({ label, value }) {
  return (
    <div className="text-xs">
      <span className="text-[10px] uppercase tracking-wider text-[#647087] mr-1">{label}:</span>
      <span className="text-[#cbd2df]">{value}</span>
    </div>
  );
}
